import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Envelope, MapPin, Clock, PaperPlaneTilt, Upload, X, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  { value: "platrerie-isolation", label: "Plâtrerie & Isolation" },
  { value: "peinture-decoration", label: "Peinture & Décoration" },
  { value: "revetements", label: "Revêtements de Sols & Murs" },
  { value: "ravalement-facades", label: "Ravalement de Façades" },
  { value: "autre", label: "Autre / Plusieurs services" },
];

const contactInfo = [
  {
    icon: Phone,
    title: "Téléphone",
    value: "03 85 39 29 38",
    link: "tel:0385392938",
    description: "Du lundi au vendredi",
  },
  {
    icon: Envelope,
    title: "Email",
    value: "qualideco@quali-deco.fr",
    link: "mailto:qualideco@quali-deco.fr",
    description: "Réponse sous 24h",
  },
  {
    icon: MapPin,
    title: "Adresse",
    value: "306 Rue Ampère, 71000 Mâcon",
    link: "https://maps.google.com/?q=306+Rue+Ampère+71000+Mâcon",
    description: "Saône-et-Loire, France",
  },
  {
    icon: Clock,
    title: "Horaires",
    value: "Lun-Ven: 8h30-12h / 13h30-17h",
    link: null,
    description: "Fermé le week-end",
  },
];

export default function ContactPage() {
  const [formType, setFormType] = useState("quote"); // 'quote' or 'contact'
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service_type: "",
    description: "",
  });
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter((file) => {
      const isValid = file.type.startsWith("image/");
      const isSmall = file.size < 10 * 1024 * 1024; // 10MB
      if (!isValid) toast.error(`${file.name} n'est pas une image valide`);
      if (!isSmall) toast.error(`${file.name} est trop volumineux (max 10MB)`);
      return isValid && isSmall;
    });
    setFiles((prev) => [...prev, ...validFiles].slice(0, 5));
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      Object.entries(quoteForm).forEach(([key, value]) => {
        formData.append(key, value);
      });
      files.forEach((file) => {
        formData.append("photos", file);
      });

      const response = await axios.post(`${API}/quote`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Demande envoyée !", {
        description: response.data.message,
      });

      setQuoteForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        service_type: "",
        description: "",
      });
      setFiles([]);
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Erreur", {
        description: "Une erreur est survenue. Veuillez réessayer.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, contactForm);

      toast.success("Message envoyé !", {
        description: response.data.message,
      });

      setContactForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting contact:", error);
      toast.error("Erreur", {
        description: "Une erreur est survenue. Veuillez réessayer.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-[#F8F9FA] py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Contact
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#333333] mt-3">
              Parlons de Votre Projet
            </h1>
            <p className="text-[#64748B] text-lg mt-6">
              Demandez un devis gratuit ou contactez-nous pour toute question. 
              Notre équipe vous répond sous 24 heures.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b border-gray-100" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              const Wrapper = info.link ? "a" : "div";
              return (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Wrapper
                    href={info.link || undefined}
                    target={info.link?.startsWith("http") ? "_blank" : undefined}
                    rel={info.link?.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="flex items-start gap-4 p-6 bg-[#F8F9FA] rounded-sm hover:bg-[#F1F5F9] transition-colors group"
                    data-testid={`contact-info-${info.title.toLowerCase()}`}
                  >
                    <div className="bg-[#1B93A4] p-3 rounded-sm">
                      <Icon size={24} weight="fill" className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#333333] group-hover:text-[#E74C3C] transition-colors">
                        {info.title}
                      </h3>
                      <p className="text-[#333333] text-sm mt-1">{info.value}</p>
                      <p className="text-[#64748B] text-xs mt-1">{info.description}</p>
                    </div>
                  </Wrapper>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              {/* Form Type Toggle */}
              <div className="flex gap-4 mb-8">
                <button
                  onClick={() => setFormType("quote")}
                  className={`flex-1 py-4 px-6 rounded-sm font-bold uppercase tracking-wide text-sm transition-all ${
                    formType === "quote"
                      ? "bg-[#E74C3C] text-white"
                      : "bg-[#F8F9FA] text-[#333333] hover:bg-[#E2E8F0]"
                  }`}
                  data-testid="toggle-quote-form"
                >
                  Demande de Devis
                </button>
                <button
                  onClick={() => setFormType("contact")}
                  className={`flex-1 py-4 px-6 rounded-sm font-bold uppercase tracking-wide text-sm transition-all ${
                    formType === "contact"
                      ? "bg-[#E74C3C] text-white"
                      : "bg-[#F8F9FA] text-[#333333] hover:bg-[#E2E8F0]"
                  }`}
                  data-testid="toggle-contact-form"
                >
                  Message Simple
                </button>
              </div>

              {/* Quote Form */}
              {formType === "quote" && (
                <motion.form
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  onSubmit={handleQuoteSubmit}
                  className="space-y-6"
                  data-testid="quote-form"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="quote-name">Nom complet *</Label>
                      <Input
                        id="quote-name"
                        type="text"
                        placeholder="Jean Dupont"
                        value={quoteForm.name}
                        onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                        required
                        className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4]"
                        data-testid="quote-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quote-email">Email *</Label>
                      <Input
                        id="quote-email"
                        type="email"
                        placeholder="jean@example.com"
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                        required
                        className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4]"
                        data-testid="quote-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="quote-phone">Téléphone *</Label>
                      <Input
                        id="quote-phone"
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                        required
                        className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4]"
                        data-testid="quote-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quote-service">Type de travaux *</Label>
                      <Select
                        value={quoteForm.service_type}
                        onValueChange={(value) => setQuoteForm({ ...quoteForm, service_type: value })}
                        required
                      >
                        <SelectTrigger className="bg-[#F8F9FA] border-none" data-testid="quote-service-select">
                          <SelectValue placeholder="Sélectionnez un service" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quote-address">Adresse du chantier *</Label>
                    <Input
                      id="quote-address"
                      type="text"
                      placeholder="123 rue de la République, 71000 Mâcon"
                      value={quoteForm.address}
                      onChange={(e) => setQuoteForm({ ...quoteForm, address: e.target.value })}
                      required
                      className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4]"
                      data-testid="quote-address-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quote-description">Description du projet *</Label>
                    <Textarea
                      id="quote-description"
                      placeholder="Décrivez votre projet en détail : surface, état actuel, résultat souhaité..."
                      value={quoteForm.description}
                      onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })}
                      required
                      rows={5}
                      className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4] resize-none"
                      data-testid="quote-description-input"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="space-y-2">
                    <Label>Photos (optionnel, max 5)</Label>
                    <div className="border-2 border-dashed border-[#E2E8F0] rounded-sm p-6 text-center hover:border-[#1B93A4] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                        data-testid="quote-file-input"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload size={40} className="mx-auto text-[#64748B] mb-3" />
                        <p className="text-[#333333] font-medium">
                          Cliquez pour ajouter des photos
                        </p>
                        <p className="text-[#64748B] text-sm mt-1">
                          PNG, JPG jusqu'à 10MB
                        </p>
                      </label>
                    </div>

                    {/* File Preview */}
                    {files.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-4">
                        {files.map((file, index) => (
                          <div
                            key={index}
                            className="relative group bg-[#F8F9FA] p-2 rounded-sm"
                          >
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="w-20 h-20 object-cover rounded-sm"
                            />
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="absolute -top-2 -right-2 bg-[#E74C3C] text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`remove-file-${index}`}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide py-6 rounded-sm text-base"
                    data-testid="quote-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Envoi en cours...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <PaperPlaneTilt size={20} weight="bold" />
                        Envoyer ma Demande de Devis
                      </span>
                    )}
                  </Button>
                </motion.form>
              )}

              {/* Contact Form */}
              {formType === "contact" && (
                <motion.form
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  onSubmit={handleContactSubmit}
                  className="space-y-6"
                  data-testid="contact-form"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name">Nom complet *</Label>
                      <Input
                        id="contact-name"
                        type="text"
                        placeholder="Jean Dupont"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4]"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email">Email *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        placeholder="jean@example.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4]"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contact-phone">Téléphone</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4]"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-subject">Sujet *</Label>
                      <Input
                        id="contact-subject"
                        type="text"
                        placeholder="Votre sujet"
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                        required
                        className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4]"
                        data-testid="contact-subject-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      placeholder="Votre message..."
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      rows={6}
                      className="bg-[#F8F9FA] border-none focus:ring-2 focus:ring-[#1B93A4] resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide py-6 rounded-sm text-base"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Envoi en cours...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <PaperPlaneTilt size={20} weight="bold" />
                        Envoyer mon Message
                      </span>
                    )}
                  </Button>
                </motion.form>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              {/* Why Contact Us */}
              <div className="bg-[#F8F9FA] p-8 rounded-sm">
                <h3 className="text-xl font-bold text-[#333333] mb-6">
                  Pourquoi Nous Contacter ?
                </h3>
                <ul className="space-y-4">
                  {[
                    "Devis gratuit et sans engagement",
                    "Réponse sous 24 heures",
                    "Conseils personnalisés",
                    "Visite sur site offerte",
                    "Accompagnement complet",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle size={20} weight="fill" className="text-[#1B93A4] flex-shrink-0 mt-0.5" />
                      <span className="text-[#333333] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Map Link */}
              <div className="bg-[#333333] p-8 rounded-sm text-white">
                <h3 className="text-xl font-bold mb-4">Nous Trouver</h3>
                <p className="text-gray-300 mb-6">
                  306 rue Ampère<br />
                  71000 Mâcon<br />
                  France
                </p>
                <a
                  href="https://maps.google.com/?q=306+rue+Ampère+71000+Mâcon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#1B93A4] hover:bg-[#1B93A4]/80 text-white font-medium px-6 py-3 rounded-sm transition-colors"
                  data-testid="google-maps-btn"
                >
                  <MapPin size={20} />
                  Voir sur Google Maps
                </a>
              </div>
</div>
          </div>
        </div>
      </section>
    </div>
  );
}
